import { Input } from '@builder.io/sdk';

import moleculeAwardsFields, { AwardsItemConfigProps } from 'molecules/Awards/config';
import { formFields } from 'molecules/Form/config';
import { moleculeHeadingFields } from 'molecules/Heading/config';
import imageField from 'molecules/Image/config';
import sectionFields, { SectionConfigProps } from 'molecules/Section/config';
import { videoCustomFields } from 'molecules/VideoCustom/config';

import pricingCardFields, { PricingCardConfigProps } from 'organisms/Cards/Pricing/config';

import { trustBarsFields } from 'components/Trustbar/config';

const heroFields = [
  ...moleculeHeadingFields,
  {
    name: 'variant',
    type: 'string',
    enum: [
      { label: 'Custom', value: 'custom' },
      { label: 'Centered', value: 'centered' },
      { label: 'Centered (Background)', value: 'centeredBackground' },
      { label: 'LeftAligned', value: 'leftAligned' },
      { label: 'Split Lottie', value: 'splitLottie' },
      { label: 'Pricing', value: 'pricing' },
      { label: 'Trust Bar', value: 'trustBar' },
      { label: 'Form', value: 'form' },
      { label: 'Featured Resource', value: 'featuredResource' },
      { label: 'Carousel', value: 'carousel' },
      { label: 'Demo Video', value: 'demoVideo' },
      { label: 'Calendar', value: 'calendar' },
    ],
    defaultValue: 'centered',
    helperText: 'Choose the layout type for the hero section. Each variant reveals different fields below.',
  },
  {
    name: 'mediaType',
    type: 'string',
    enum: [
      { label: 'Image', value: 'image' },
      { label: 'Video', value: 'video' },
      { label: 'Lottie', value: 'lottie' },
    ],
    defaultValue: 'image',
    helperText: 'Choose whether to display an image, video, or Lottie animation in the hero section.',
    showIf:
      "['centered', 'centeredBackground', 'leftAligned', 'splitLottie', 'trustBar', 'custom', 'demoVideo'].includes(options.get('variant'))",
  },
  imageField(
    'featuredImage',
    "['centered', 'centeredBackground', 'leftAligned', 'splitLottie', 'trustBar', 'custom', 'demoVideo'].includes(options.get('variant')) && options.get('mediaType') === 'image'",
  ),
  {
    ...imageField(
      'featuredImageMobile',
      "['centered', 'centeredBackground', 'leftAligned', 'splitLottie', 'trustBar', 'custom', 'demoVideo'].includes(options.get('variant')) && options.get('mediaType') === 'image'",
    ),
    helperText: 'Optional. Mobile-specific image (≤640px). If not defined, the main image will be used as fallback.',
  },
  ...videoCustomFields.map(field => ({
    ...field,
    showIf: "['centered', 'demoVideo'].includes(options.get('variant')) && options.get('mediaType') === 'video'",
  })),
  {
    name: 'lottieType',
    type: 'string',
    enum: [
      { label: 'JSON', value: 'json' },
      { label: '.lottie', value: 'lottie' },
    ],
    defaultValue: 'json',
    helperText: 'Choose the Lottie file format. JSON is the standard format, .lottie is a compressed format.',
    showIf:
      "['centered', 'centeredBackground', 'leftAligned', 'splitLottie', 'trustBar', 'custom', 'demoVideo'].includes(options.get('variant')) && options.get('mediaType') === 'lottie'",
  },
  {
    name: 'lottieDesktop',
    type: 'file',
    allowedFileTypes: ['json', 'lottie'],
    helperText: 'Desktop Lottie animation file (~1200×700px). JSON or .lottie format.',
    showIf:
      "['centered', 'centeredBackground', 'leftAligned', 'splitLottie', 'trustBar', 'custom', 'demoVideo'].includes(options.get('variant')) && options.get('mediaType') === 'lottie'",
  },
  {
    name: 'lottieMobile',
    type: 'file',
    allowedFileTypes: ['json', 'lottie'],
    helperText:
      'Optional. Mobile Lottie animation file (~390-420×700-800px). JSON or .lottie format. If not provided, desktop animation will be used.',
    showIf:
      "['centered', 'centeredBackground', 'leftAligned', 'splitLottie', 'trustBar', 'custom', 'demoVideo'].includes(options.get('variant')) && options.get('mediaType') === 'lottie'",
  },
  {
    name: 'lottieLoop',
    type: 'boolean',
    defaultValue: true,
    helperText: 'Enable looping for Lottie animations.',
    showIf:
      "['centered', 'centeredBackground', 'leftAligned', 'splitLottie', 'trustBar', 'custom', 'demoVideo'].includes(options.get('variant')) && options.get('mediaType') === 'lottie'",
  },
  {
    name: 'lottieAutoplay',
    type: 'boolean',
    defaultValue: true,
    helperText: 'Auto-play Lottie animations on page load.',
    showIf:
      "['centered', 'centeredBackground', 'leftAligned', 'splitLottie', 'trustBar', 'custom', 'demoVideo'].includes(options.get('variant')) && options.get('mediaType') === 'lottie'",
  },
  {
    name: 'leftGradientColor',
    type: 'color',
    defaultValue: '#0a2f5d',
    helperText: 'Color of the blurry gradient circle on the left side behind the media. Split Lottie variant only.',
    showIf: "options.get('variant') === 'splitLottie'",
  },
  {
    name: 'rightGradientColor',
    type: 'color',
    defaultValue: '#09676f',
    helperText: 'Color of the blurry gradient circle on the right side behind the media. Split Lottie variant only.',
    showIf: "options.get('variant') === 'splitLottie'",
  },
  {
    name: 'heroStats',
    type: 'list',
    helperText: 'Stat row displayed under the buttons. Recommended: 3 items. Split Lottie variant only.',
    showIf: "options.get('variant') === 'splitLottie'",
    defaultValue: [
      { value: '10x', description: 'faster build' },
      { value: '95%', description: 'less dev time' },
      { value: '5x', description: 'ARPU lift' },
    ],
    subFields: [
      {
        name: 'value',
        type: 'string',
        required: true,
        defaultValue: '10x',
        helperText: 'The statistic itself (e.g., "10x", "95%", "5x").',
      },
      {
        name: 'description',
        type: 'string',
        required: true,
        defaultValue: 'faster build',
        helperText: 'Short label under the statistic (e.g., "faster build").',
      },
    ],
  },
  {
    name: 'cards',
    type: 'list',
    subFields: [{ name: 'id', type: 'string', required: true, showIf: 'false' }, ...pricingCardFields],
    helperText: 'Pricing card set (max 3). Only visible if variant is "Pricing".',
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    onChange: (options: Map<string, any>) => {
      if (options.get('cards').length > 0) {
        options.get('cards').forEach((item: Map<string, string>, index: number) => {
          item.set('id', `card-${index}`);
        });
      }
      if (options.get('cards').length > 3) {
        options.set('cards', options.get('cards').slice(0, 3));
        alert('Maximum card count is 3. Removing excess cards.');
      }
    },
    showIf: "options.get('variant') === 'pricing'",
  },
  {
    name: 'carousel',
    type: 'list',
    subFields: [
      { name: 'id', type: 'string', required: true, showIf: 'false' },
      { name: 'resource', type: 'reference', helperText: 'Select a resource to include in the carousel.' },
    ],
    helperText: 'Carousel of featured resources (max 6). Only visible if variant is "Carousel".',
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    onChange: (options: Map<string, any>) => {
      if (options.get('resources').length > 0) {
        options.get('resources').forEach((item: Map<string, string>, index: number) => {
          item.set('id', `resource-${index}`);
        });
      }
      if (options.get('resources').length > 6) {
        options.set('resources', options.get('resources').slice(0, 6));
        alert('Maximum resource count is 6. Removing excess resources.');
      }
    },
    showIf: "options.get('variant') === 'carousel'",
  },
  {
    name: 'trustBar',
    type: 'object',
    subFields: trustBarsFields,
    showIf: "options.get('variant') === 'trustBar'",
    helperText: 'Trust bar logos and configuration. Only visible if variant is "Trust Bar".',
  },
  {
    name: 'featuredResource',
    type: 'reference',
    showIf: "options.get('variant') === 'featuredResource'",
    helperText: 'Select a featured resource to display. Only visible if variant is "Featured Resource".',
  },
  {
    name: 'eventDate',
    type: 'string',
    showIf: "options.get('variant') === 'form'",
    helperText: 'Event date text displayed with the form variant.',
  },
  {
    name: 'eventTime',
    type: 'string',
    showIf: "options.get('variant') === 'form'",
    helperText: 'Event time text displayed with the form variant.',
  },
  {
    name: 'isFormStyling',
    type: 'boolean',
    defaultValue: false,
    helperText:
      'Enable compact form styling. When enabled, applies optimized spacing and typography for form sections (20px intro/steps, 16px descriptions, tighter gaps).',
    showIf: "options.get('variant') === 'form'",
  },
  ...moleculeAwardsFields("options.get('variant') === 'form'"),
  ...formFields("['form', 'demoVideo'].includes(options.get('variant'))"),
  {
    name: 'secondaryCta',
    type: 'object',
    helperText: 'Optional text link below the form or buttons (e.g., "Or, take a self-guided product tour →")',
    showIf: "['form', 'demoVideo', 'leftAligned'].includes(options.get('variant'))",
    subFields: [
      {
        name: 'text',
        type: 'string',
        helperText: 'Link text (e.g., "Or, take a self-guided product tour")',
      },
      {
        name: 'url',
        type: 'string',
        helperText: 'Link destination URL',
      },
      {
        name: 'openInNewTab',
        type: 'boolean',
        defaultValue: false,
        helperText: 'Open link in new tab',
      },
    ],
  },
  {
    name: 'checkItems',
    type: 'list',
    helperText: 'Checkmark list shown below the heading. Calendar variant only.',
    showIf: "options.get('variant') === 'calendar'",
    subFields: [
      {
        name: 'text',
        type: 'string',
        required: true,
        helperText: 'Text for this checkmark item.',
      },
    ],
  },
  {
    name: 'testimonial',
    type: 'object',
    helperText: 'Optional testimonial quote with company logo. Calendar variant only.',
    showIf: "options.get('variant') === 'calendar'",
    subFields: [{ name: 'quote', type: 'longText', helperText: 'The testimonial quote text.' }, imageField('logo')],
  },
  {
    ...imageField('logosImage', "options.get('variant') === 'calendar'"),
    helperText: 'Optional integration logos strip displayed at the bottom of the left column. Calendar variant only.',
  },
  {
    name: 'scheduler',
    type: 'object',
    helperText: 'Embedded scheduler on the right column. Calendar variant only.',
    showIf: "options.get('variant') === 'calendar'",
    subFields: [
      {
        name: 'provider',
        type: 'string',
        enum: [
          { label: 'Chili Piper (marketing.js)', value: 'chilipiper' },
          { label: 'Chili Piper (round-robin iframe)', value: 'chilipiper-roundrobin' },
          { label: 'Calendly', value: 'calendly' },
        ],
        defaultValue: 'chilipiper',
        helperText:
          'Pick the scheduler provider. "Chili Piper (marketing.js)" uses the SDK with tenant/router. "Chili Piper (round-robin iframe)" embeds the public round-robin URL directly. Calendly uses an inline iframe.',
      },
      {
        name: 'chiliPiperTenant',
        type: 'string',
        defaultValue: 'prismatic',
        helperText:
          'Chili Piper (marketing.js) only. Tenant slug (e.g. "prismatic"). Used by ChiliPiper.submit(tenant, router, ...).',
      },
      {
        name: 'chiliPiperRouter',
        type: 'string',
        defaultValue: 'ae-intro-call-router',
        helperText: 'Chili Piper (marketing.js) only. Router name (e.g. "ae-intro-call-router").',
      },
      {
        name: 'embedUrl',
        type: 'string',
        helperText:
          'Calendly or Chili Piper round-robin only. Full inline embed URL (e.g. https://calendly.com/.../...?embed_type=Inline or https://prismatic.chilipiper.com/round-robin/inbound-intro-call).',
      },
      {
        name: 'fallbackUrl',
        type: 'string',
        helperText:
          'Optional. Public scheduling URL opened in a new tab when the inline embed fails to load (e.g. user has an ad-blocker). For Chili Piper, paste the router public link.',
      },
      {
        name: 'height',
        type: 'number',
        defaultValue: 700,
        helperText: 'Embed height in pixels on desktop.',
      },
      {
        name: 'title',
        type: 'string',
        defaultValue: 'Schedule a demo',
        helperText: 'Accessible title for the scheduler container.',
      },
    ],
  },
  ...sectionFields,
] as const satisfies DeepReadonly<Input[]>;

const heroConfig = {
  name: 'Hero',
  inputs: heroFields,
};

export type HeroConfigProps = SectionConfigProps &
  GenerateBuilder<typeof heroFields> & {
    featuredResource?: BuilderReference<BlogResource>;
    carousel?: { id: string; resource: BuilderReference<BlogResource> }[];
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    trustBar?: any;
    lottieType?: 'json' | 'lottie';
    lottieDesktop?: string;
    lottieMobile?: string;
    lottieLoop?: boolean;
    lottieAutoplay?: boolean;
    leftGradientColor?: string;
    rightGradientColor?: string;
    mediaType?: 'image' | 'video' | 'lottie';
    isFormStyling?: boolean;
    eventDate?: string;
    eventTime?: string;
    awardItems?: AwardsItemConfigProps[];
    form?: BuilderReference<FormReference>;
    cards?: (PricingCardConfigProps & { id: string })[];
    heroStats?: { value?: string; description?: string }[];
    videoSrc?: string;
    thumbnail?: {
      file?: {
        url: string;
        width?: number;
        height?: number;
      };
      alt?: string;
    };
    secondaryCta?: {
      text?: string;
      url?: string;
      openInNewTab?: boolean;
    };
  };
export type HeroWithCardsConfigProps = Omit<
  HeroConfigProps,
  'featuredImage' | 'trustBar' | 'awardItems' | 'form' | 'featuredResource' | 'carousel'
>;
export type HeroWithImageConfigProps = Omit<
  HeroConfigProps,
  'cards' | 'trustBar' | 'awardItems' | 'form' | 'featuredResource' | 'carousel'
>;
export type HeroWithTrustBarConfigProps = Omit<
  HeroConfigProps,
  'cards' | 'awardItems' | 'form' | 'featuredResource' | 'carousel'
>;
export type HeroWithFormConfigProps = Omit<
  HeroConfigProps,
  'cards' | 'trustBar' | 'featuredImage' | 'featuredResource' | 'carousel'
>;
export type HeroWithFeaturedResourceConfigProps = Omit<
  HeroConfigProps,
  'cards' | 'trustBar' | 'awardItems' | 'form' | 'carousel'
>;
export type HeroWithCarouselConfigProps = Omit<
  HeroConfigProps,
  'cards' | 'trustBar' | 'awardItems' | 'form' | 'featuredResource' | 'featuredImage'
>;
export type HeroDemoVideoConfigProps = Omit<
  HeroConfigProps,
  'cards' | 'trustBar' | 'awardItems' | 'featuredResource' | 'carousel'
>;
export type HeroCalendarConfigProps = Omit<
  HeroConfigProps,
  'cards' | 'trustBar' | 'awardItems' | 'form' | 'featuredResource' | 'carousel' | 'featuredImage'
> & {
  checkItems?: { text: string }[];
  testimonial?: {
    quote?: string;
    logo?: {
      file?: { url?: string; width?: number; height?: number };
      alt?: string;
    };
  };
  logosImage?: {
    file?: { url?: string; width?: number; height?: number };
    alt?: string;
  };
  scheduler?: {
    provider?: 'chilipiper' | 'chilipiper-roundrobin' | 'calendly';
    chiliPiperTenant?: string;
    chiliPiperRouter?: string;
    embedUrl?: string;
    fallbackUrl?: string;
    height?: number;
    title?: string;
  };
};

export default heroConfig;
